import { styled } from "@linaria/react"
import { useState } from "react"

import { Badge } from "@/components/Badge/Badge.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { Card } from "@/components/Card/Card.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { CommitRegular, DeleteLight, Icon20 } from "@/icons/Icons.ts"
import { formatDateTime, formatDuration } from "@/lib/date.ts"
import { shortRevisionId } from "@/lib/displayUtils.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type {
  UWarmupSnapshotApiState,
  WarmupSnapshot,
  WarmupSnapshotApiState
} from "@/lib/client/generated/TypeDefinitions.ts"

const formatElapsedTime = (startedAt: Date) => {
  const elapsedMs = Date.now() - startedAt.getTime()
  return formatDuration(elapsedMs)
}

export const WarmupSnapshotItem = (props: {
  snapshot: WarmupSnapshot
  deleteSnapshot: (snapshotId: string) => void
  canEditProject: boolean
}) => {
  const [hovered, setHovered] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  return (
    <SnapshotItemCard
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Vertical gap="4px">
        <FirstLine>
          <CommitRegular className={Icon20} />
          <SnapshotTitle>{shortRevisionId(props.snapshot.commitHash)}</SnapshotTitle>

          <Spring />

          {isFailed(props.snapshot.status) && <SnapshotStatus kind="error">FAILED</SnapshotStatus>}
          {isActive(props.snapshot.status) && <SnapshotStatus kind="success">ACTIVE</SnapshotStatus>}
          {isInProgress(props.snapshot.status) && <SnapshotStatus kind="warning">IN PROGRESS</SnapshotStatus>}
        </FirstLine>

        <SecondLine>
          <SnapshotDate>{formatDateTime(props.snapshot.createdAt)}</SnapshotDate>

          <Spring />

          <SnapshotStatusDetailsContainer visible={!hovered || (hovered && !props.canEditProject)}>
            {isFailed(props.snapshot.status) && (
              <SnapshotStatusDetailsFailed>
                Duration: {props.snapshot.status.finishedAt ? formatDuration(props.snapshot.status.finishedAt - props.snapshot.createdAt) : "N/A"}
              </SnapshotStatusDetailsFailed>
            )}

            {isActive(props.snapshot.status) && (
              <SnapshotStatusDetailsActive>
                Duration: {props.snapshot.status.finishedAt ? formatDuration(props.snapshot.status.finishedAt - props.snapshot.createdAt) : "N/A"}
              </SnapshotStatusDetailsActive>
            )}

            {isInProgress(props.snapshot.status) && (
              <SnapshotStatusDetailsInProgress>
                Running: {formatElapsedTime(new Date(props.snapshot.status.startedAt))}
              </SnapshotStatusDetailsInProgress>
            )}
          </SnapshotStatusDetailsContainer>
          {props.canEditProject && (
            <HoverActions visible={hovered}>
              <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={setConfirmDialogOpen}
                trigger={
                  <Tooltip content="Delete">
                    <Button tabIndex={hovered ? undefined : -1} kind="icon" onClick={(e) => {
                      e.stopPropagation()
                      setConfirmDialogOpen(true)
                    }}><DeleteLight /></Button>
                  </Tooltip>
                }
                description="Are you sure you want to delete this snapshot?"
                confirmText="Delete"
                onConfirm={() => {
                  props.deleteSnapshot(props.snapshot.id)
                  setConfirmDialogOpen(false)
                }}
              />
            </HoverActions>
          )}
        </SecondLine>
      </Vertical>
    </SnapshotItemCard>
  )
}

const FirstLine = styled(Horizontal)`
  align-items: center;
  gap: 4px;

  line-height: 24px;
`

const SecondLine = styled(Horizontal)`
  align-items: center;
  gap: 4px;

  position: relative;

  color: var(--secondary-text-color);

  ${textStyles.smallFontSize};
`

const HoverActions = styled(Horizontal)<{ visible: boolean }>`
  position: absolute;
  right: 0;

  align-items: center;
  gap: 16px;

  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? "0" : "8px"});
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
`

const SnapshotItemCard = styled(Card)`
  padding: 8px 16px;
  border-radius: var(--border-radius-16);
`

const SnapshotTitle = styled.div`
  ${textStyles.baseFontSize};
  font-weight: 500;
`

const SnapshotDate = styled.div`
  ${textStyles.smallerFontSize};
  color: var(--secondary-text-color);
`

const SnapshotStatus = styled(Badge)`
  ${textStyles.smallestFontSize};
  border-radius: var(--border-radius-8);
  font-weight: 500;
`

const SnapshotStatusDetailsContainer = styled.div<{ visible: boolean }>`
  ${textStyles.smallerFontSize};
  color: var(--secondary-text-color);

  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? "0" : "-8px"});
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  cursor: ${props => props.visible ? "text" : "default"};
`

const SnapshotStatusDetailsActive = styled.div`
`

const SnapshotStatusDetailsInProgress = styled.div`
`

const SnapshotStatusDetailsFailed = styled.div`
`

function isFailed(status: UWarmupSnapshotApiState): status is WarmupSnapshotApiState.Failed {
  return status.className === "Failed"
}

function isActive(status: UWarmupSnapshotApiState): status is WarmupSnapshotApiState.Active {
  return status.className === "Active"
}

function isInProgress(status: UWarmupSnapshotApiState): status is WarmupSnapshotApiState.InProgress {
  return status.className === "InProgress"
}
